import { Form, Formik, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { useAccount } from '@azure/msal-react';
import { CircularProgress } from '@mui/material';

import { openPdf, withQueryParams } from '@shared/commonFunctions';
import { Modal } from '@shared/components/modals/Modal';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { getError } from '@shared/utils/helpers';
import { VehicleFeaturesLoaderWrapper } from '@styles/Shared/StyledLoaders';

import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { IBookResponse, IVehicleFeaturesModalProps, IVehicleFeature, VALUATION_TYPES, VALUATION_TEXTS, IFormAsset } from '../interfaces';
import { requestBookoutPdf } from '../requests';
import { KbbForm } from './KbbForm';
import { NadaForm } from './NadaForm';

export const VehicleFeaturesModal = ({
  handleClose,
  valuationType,
  zip,
  autoSubmitOptionsPopup,
  handleValuationPull,
  vehicleFeatureModalPosition,
  pickedDealId
}: IVehicleFeaturesModalProps) => {
  const { values: assetData, setFieldValue } = useFormikContext<IFormAsset>();
  const accountInfo = useAccount();

  const { id, contactId, vin, mileage, assetRegistration, nadaTrim, kbbTrim } = assetData;
  const url = withQueryParams(
    !autoSubmitOptionsPopup ? `${SERVICES.ASSET}/assets/${id}/bumper-valuation-ai`: `${SERVICES.ASSET}/assets/${id}/valuation`,
    {
      vinNumber: vin,
      contactId,
      state: assetRegistration?.state,
      mileage,
      assetValuationType: valuationType,
      trim: valuationType === VALUATION_TYPES.Nada ? nadaTrim : kbbTrim,
      zipCode: zip,
      isDefault: autoSubmitOptionsPopup
    },
    false
  );

  const { data: featureData, error = null, isValidating } = useSWR<IVehicleFeature>(url);
  const [vehicleData, setVehicleData] = useState<IVehicleFeature>({} as IVehicleFeature);
  const [loading, setLoading] = useState(false);
  const [valuationPullSuccess, setValuationPullSuccess] = useState(true);
  
  useEffect(() => {
    if (!featureData && error && !isValidating) {
      Notistack.generalError(getError(error));
      handleClose();
    }
  }, [error, featureData, isValidating]);

  useEffect(() => {
    const vehicleFeatures = { ...featureData } || {};

    if (featureData && valuationType) {
      let features: string[] = [];

      // vehicle features options
      if (valuationType === VALUATION_TYPES.Nada && featureData.vehicleValuation && featureData.vehicleValuation.options) {
        features = featureData.vehicleValuation?.options.filter((option) => option.isSelected || option.isMatchBumper).map((option) => option.id);
      } else if (
        valuationType === VALUATION_TYPES.Kbb &&
        featureData.vehicleDetails &&
        featureData.vehicleDetails.vehicleOptions
      ) {
        features = featureData.vehicleDetails?.vehicleOptions
          .filter((option) => option.isSelected || option.isMatchBumper)
          .map((option) => option.vehicleOptionId.toString());
      }
      vehicleFeatures.selectedOptions = features;

      // valuation
      let valuationPullResult = true;
      if (autoSubmitOptionsPopup) {
        if (valuationType === VALUATION_TYPES.Kbb) {
          if (vehicleFeatures.retailValue != null && vehicleFeatures.wholesaleValue != null) {
            setFieldValue('kbb.baseRetailAmount', vehicleFeatures.retailValue);
            setFieldValue('kbb.baseWholesaleAmount', vehicleFeatures.wholesaleValue);
            setFieldValue('kbb.baseFinanceExpressAmount', vehicleFeatures.financeExpressValue);
          } else {
            setFieldValue('kbb.baseRetailAmount', '');
            setFieldValue('kbb.baseWholesaleAmount', '');
            setFieldValue('kbb.baseFinanceExpressAmount', '');
            Notistack.generalError('KBB - Valuation not found');
            valuationPullResult = false;
          }
        } else if (valuationType === VALUATION_TYPES.Nada) {
          if (vehicleFeatures.retailValue != null && vehicleFeatures.tradeInValue != null) {
            setFieldValue('nada.baseRetailAmount', vehicleFeatures.retailValue);
            setFieldValue('nada.baseTradeInAmount', vehicleFeatures.tradeInValue);
          } else {
            Notistack.generalError('Nada - Valuation not found');
            setFieldValue('nada.baseRetailAmount', '');
            setFieldValue('nada.baseTradeInAmount', '');
            valuationPullResult = false;
          }
        }
      }
      setValuationPullSuccess(valuationPullResult);
      // auto pull - generate bookout
      if (autoSubmitOptionsPopup) {
        submitVehicleFeatures(vehicleFeatures as IVehicleFeature, valuationPullResult);
      }
    }
    setVehicleData(vehicleFeatures as IVehicleFeature);
    setFieldValue('bumper.high', vehicleFeatures?.high);
    setFieldValue('bumper.avg', vehicleFeatures?.avg);
    setFieldValue('bumper.low', vehicleFeatures?.low);
  }, [featureData, valuationType, autoSubmitOptionsPopup]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleSubmitForm = () => {};

  const submitVehicleFeatures = async (data: IVehicleFeature, valuationPullSuccess: boolean) => {
    const postData = {
      ...data,
      contactId,
      mileage,
      vinNumber: vin,
      state: assetRegistration?.state,
      assetValuationType: valuationType,
      userId: accountInfo?.localAccountId,
      dealId: pickedDealId
    };
    setLoading(true);
    const res: IBookResponse = await requestBookoutPdf(postData, id, vin, valuationType);
    
    if (res) {
      await setFieldValue('bookOutDate', res.bookOutDate);
      if (valuationType === VALUATION_TYPES.Kbb) {
        await setFieldValue('kbb.baseWholesaleAmount', res.wholeSaleAmount);
        await setFieldValue('kbb.baseRetailAmount', res.retailAmount);
        await setFieldValue('kbb.baseFinanceExpressAmount', res.financeExpressAmount);
      } else if (valuationType === VALUATION_TYPES.Nada) {
        await setFieldValue('nada.baseTradeInAmount', res.tradeInAmount);
        await setFieldValue('nada.baseRetailAmount', res.retailAmount);
      }
      if (!autoSubmitOptionsPopup) {
        openPdf(res.content, res.fileName);
      }
      handleValuationPull(valuationPullSuccess);
    }
    setLoading(true);
    handleClose();
  };

  return (
    <>
     {autoSubmitOptionsPopup && <LoaderBackdrop open={(!featureData && !error) || isValidating || loading} />}
      <Modal
        hideBackdrop
        open={!autoSubmitOptionsPopup}
        resetPosition={vehicleFeatureModalPosition}
        title={`${VALUATION_TEXTS[valuationType as keyof typeof VALUATION_TEXTS]} Vehicle Features`}
        dialogHeight="600px"
        dialogWidth="650px"
        onClose={handleClose}
      >
        {(!featureData && !error) || isValidating ? (
          <VehicleFeaturesLoaderWrapper>
            <CircularProgress color="secondary" />
          </VehicleFeaturesLoaderWrapper>
        ) : (
          <Formik
            enableReinitialize
            initialValues={vehicleData}
            onSubmit={handleSubmitForm}
          >
            {(props) => (
              <Form style={{ position: 'initial'}}>
                {vehicleData?.vehicleValuation?.options &&
                  props.values.selectedOptions &&
                  valuationType === VALUATION_TYPES.Nada && (
                    <NadaForm
                      options={vehicleData?.vehicleValuation?.options}
                      value={props.values}
                      handleFeatureSubmit={(data) => submitVehicleFeatures(data, valuationPullSuccess)}
                    />
                  )}

                {vehicleData?.vehicleDetails?.vehicleOptions &&
                  props.values.selectedOptions &&
                  valuationType === VALUATION_TYPES.Kbb && (
                    <KbbForm
                      options={vehicleData?.vehicleDetails?.vehicleOptions}
                      valuations={vehicleData?.vehicleValuation?.options}
                      value={props.values}
                      handleFeatureSubmit={(data) => submitVehicleFeatures(data, valuationPullSuccess)}
                    />
                  )}
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </>
  );
};
